import { useCallback, useRef, useState } from "react"

import {
  useDeleteSignaturePlacement,
  useUpdateSignaturePlacement,
} from "@/services/api/questionnaireService"
import { SignaturePlacement } from "@/types/questionnaire/models"
import { debounce, type DebouncedFunction } from "@/utils/optimisationUtils"

export type { SignaturePlacement }

type PlacementLayoutUpdate = Partial<
  Pick<SignaturePlacement, "x" | "y" | "width" | "height">
>

/**
 * Local snapshot of a questionnaire's placed signatures, plus debounced
 * persistence — mirrors usePlacementUpdater's shape (500ms debounce, an
 * immediate write on drag/resize end, flush() before navigating away) so the
 * two placement kinds cannot drift.
 */
export const useSignaturePlacements = (
  questionnaireId: string,
  initialPlacements: SignaturePlacement[],
  onSaveError: (error: unknown) => void,
) => {
  const [placements, setPlacements] =
    useState<SignaturePlacement[]>(initialPlacements)
  // Drives the toolbar dropdown's spinner: true while any placement save
  // (create, drag, resize or delete) is in flight — never hidden by the
  // debounce, since a debounced save only counts once it actually starts.
  const [pendingCount, setPendingCount] = useState(0)

  const { mutateAsync: upsertAsync } = useUpdateSignaturePlacement()
  const { mutateAsync: deleteAsync } = useDeleteSignaturePlacement()

  // Same shape as usePlacementUpdater: every save currently in flight, and
  // every placement id whose most recent save rejected — so flush() can wait
  // for in-flight work and still reject on an unpersisted edit.
  const inFlightSavesRef = useRef<Set<Promise<unknown>>>(new Set())
  const failedSavesRef = useRef<Set<string>>(new Set())

  const runUpsert = useCallback(
    (placement: SignaturePlacement) => {
      const promise = upsertAsync({
        questionnaireId,
        placementId: placement.id,
        placement: {
          signature_id: placement.signatureId,
          page: placement.page,
          x: placement.x,
          y: placement.y,
          width: placement.width,
          height: placement.height,
        },
      })
      inFlightSavesRef.current.add(promise)
      setPendingCount((count) => count + 1)
      promise
        .then(() => {
          failedSavesRef.current.delete(placement.id)
        })
        .catch((error) => {
          failedSavesRef.current.add(placement.id)
          onSaveError(error)
        })
        .finally(() => {
          inFlightSavesRef.current.delete(promise)
          setPendingCount((count) => count - 1)
        })
    },
    [upsertAsync, questionnaireId, onSaveError],
  )

  // Same ref-indirection usePlacementUpdater uses: useGuardedMutation's
  // mutateAsync gets a new identity every render, so the debounced function
  // below is built exactly once and always calls through this ref instead of
  // closing over the churning function directly.
  const runUpsertRef = useRef(runUpsert)
  runUpsertRef.current = runUpsert

  const debouncedUpsertRef = useRef<DebouncedFunction<
    [SignaturePlacement]
  > | null>(null)
  if (!debouncedUpsertRef.current) {
    debouncedUpsertRef.current = debounce((placement: SignaturePlacement) => {
      runUpsertRef.current(placement)
    }, 500)
  }
  const debouncedUpsert = debouncedUpsertRef.current

  const flush = useCallback(async () => {
    debouncedUpsert.flush()
    await Promise.all(Array.from(inFlightSavesRef.current))
    if (failedSavesRef.current.size > 0) {
      throw new Error("Unsaved signature placement edits could not be saved")
    }
  }, [debouncedUpsert])

  const addPlacement = useCallback(
    (placement: SignaturePlacement) => {
      setPlacements((prev) => [...prev, placement])
      runUpsert(placement)
    },
    [runUpsert],
  )

  const movePlacement = useCallback(
    (id: string, updates: PlacementLayoutUpdate, immediate: boolean) => {
      setPlacements((prev) => {
        const next = prev.map((placement) =>
          placement.id === id ? { ...placement, ...updates } : placement,
        )
        const updated = next.find((placement) => placement.id === id)
        if (updated) {
          if (immediate) {
            runUpsert(updated)
          } else {
            debouncedUpsert(updated)
          }
        }
        return next
      })
    },
    [runUpsert, debouncedUpsert],
  )

  const removePlacement = useCallback(
    (id: string) => {
      setPlacements((prev) => prev.filter((placement) => placement.id !== id))
      const promise = deleteAsync({ questionnaireId, placementId: id })
      inFlightSavesRef.current.add(promise)
      setPendingCount((count) => count + 1)
      promise
        .then(() => {
          failedSavesRef.current.delete(id)
        })
        .catch((error) => {
          failedSavesRef.current.add(id)
          onSaveError(error)
        })
        .finally(() => {
          inFlightSavesRef.current.delete(promise)
          setPendingCount((count) => count - 1)
        })
    },
    [deleteAsync, questionnaireId, onSaveError],
  )

  return {
    placements,
    isSaving: pendingCount > 0,
    addPlacement,
    movePlacement,
    removePlacement,
    flush,
  }
}
